import { useEffect, useState } from 'react';

/**
 * True only once `active` has stayed true for `delayMs`.
 *
 * For loading indicators. Most of these calls answer in well under a second on a decent connection, and a
 * spinner that appears and vanishes inside 200ms reads as a glitch rather than as progress — the screen
 * flickers and the guest cannot tell what happened. Waiting a beat before admitting to being busy means a
 * fast response looks instant, and only a genuinely slow one gets an indicator.
 */
export function useDelayedFlag(active: boolean, delayMs = 250): boolean {
    const [elapsed, setElapsed] = useState(false);

    useEffect(() => {
        if (!active) return;

        const timer = setTimeout(() => setElapsed(true), delayMs);

        /*
         * The reset lives in the cleanup rather than in the effect body, which is both what the lint rule
         * wants and the clearer place for it: this runs exactly when `active` stops being true, so the flag
         * cannot survive into a later wait and make the next one appear instantly.
         */
        return () => {
            clearTimeout(timer);
            setElapsed(false);
        };
    }, [active, delayMs]);

    return elapsed;
}
